import { useEffect, useRef, useState, useCallback } from 'react'
import { useReaderStore } from '../../store/readerStore'
import { useMarkChapterComplete } from '../../api/useManga'
import { useAuthStore } from '../../store/authStore'
import { mediaUrl } from '../../utils/media'

const INITIAL_LOAD = 5
const BATCH_SIZE = 3

export default function PageScroller({ pages, chapterId }) {
  const [visibleCount, setVisibleCount] = useState(INITIAL_LOAD)
  const sentinelRef = useRef(null)
  const pageRefs = useRef({})
  const setProgress = useReaderStore((s) => s.setProgress)
  const markCompleted = useReaderStore((s) => s.markCompleted)
  const savedPage = useReaderStore((s) => s.getProgress(chapterId))
  const didRestoreRef = useRef(false)
  const completedSentToServer = useRef(false)
  const isAuth = useAuthStore((s) => s.isAuth)
  const { mutate: markCompleteOnServer } = useMarkChapterComplete()

  const visiblePages = pages.slice(0, visibleCount)
  const lastPageIndex = pages.length - 1

  useEffect(() => {
    if (!pages.length || didRestoreRef.current) return
    didRestoreRef.current = true
    if (savedPage && savedPage > 0 && savedPage < pages.length) {
      setTimeout(() => {
        pageRefs.current[savedPage]?.scrollIntoView({ behavior: 'instant', block: 'start' })
      }, 100)
    }
  }, [pages.length, savedPage])

  // Load more pages as sentinel becomes visible
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && visibleCount < pages.length) {
          setVisibleCount((n) => Math.min(n + BATCH_SIZE, pages.length))
        }
      },
      { rootMargin: '400px' }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [visibleCount, pages.length])

  // Track current page position + mark completed on last page
  const trackPage = useCallback(
    (index) => {
      setProgress(chapterId, index)
      if (index === lastPageIndex) {
        markCompleted(chapterId)
        if (isAuth && !completedSentToServer.current) {
          completedSentToServer.current = true
          markCompleteOnServer(chapterId)
        }
      }
    },
    [chapterId, lastPageIndex, setProgress, markCompleted, isAuth, markCompleteOnServer]
  )

  useEffect(() => {
    const observers = []
    visiblePages.forEach((_, idx) => {
      const el = pageRefs.current[idx]
      if (!el) return
      const obs = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) trackPage(idx)
        },
        { threshold: 0.5 }
      )
      obs.observe(el)
      observers.push(obs)
    })
    return () => observers.forEach((o) => o.disconnect())
  }, [visiblePages.length, trackPage])

  return (
    <div className="flex flex-col items-center" style={{ background: '#000', gap: 4 }}>
      {visiblePages.map((page, idx) => (
        <div
          key={page.id || idx}
          ref={(el) => (pageRefs.current[idx] = el)}
          style={{ width: '100%', maxWidth: '800px', lineHeight: 0 }}
        >
          <img
            src={mediaUrl(page.image_url || page.url)}
            alt={`Страница ${idx + 1}`}
            style={{ width: '100%', display: 'block' }}
            loading={idx < INITIAL_LOAD ? 'eager' : 'lazy'}
          />
        </div>
      ))}

      {visibleCount < pages.length && (
        <div ref={sentinelRef} style={{ height: 1 }} />
      )}
    </div>
  )
}
